

import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RoleService, Role } from '../role.service';
import { PermissionService } from '../permission.service';
import { concatMap, finalize, forkJoin, of } from 'rxjs';
import Swal from 'sweetalert2';

// Define a clear interface for the data passed to the dialog
export interface RoleDialogData {
  id?: string;
  name?: string;
}

// Define a clear interface for the permission object
export interface Permission {
  id: number;
  name: string;
  description: string;
  group: string;
}

@Component({
  selector: 'app-role-dialog',
  templateUrl: './role-dialog.component.html',
})
export class RoleDialogComponent implements OnInit {
  form: FormGroup;
  permissions: Permission[] = [];
  selectedPermissions: number[] = [];
  loading = false;
  initialLoading = false;

  constructor(
    private fb: FormBuilder,
    private roleService: RoleService,
    private permissionService: PermissionService,
    public dialogRef: MatDialogRef<RoleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RoleDialogData
  ) {
    this.form = this.fb.group({
      name: [data?.name || '', Validators.required],
    });
  }

  ngOnInit() {
    this.initialLoading = true;
    const allPermissions$ = this.permissionService.getPermissions();
    // If we are editing a role, get its permissions. Otherwise, return an empty array.
    const rolePermissions$ = this.data?.id
      ? this.permissionService.getRolePermissions(this.data.id)
      : of([]);

    forkJoin([allPermissions$, rolePermissions$]).pipe(
      finalize(() => this.initialLoading = false)
    ).subscribe({
      next: ([all, assigned]) => {
        this.permissions = all;
        this.selectedPermissions = assigned.map((p: Permission) => p.id);
      },
      error: (err) => {
        console.error('Failed to load permissions', err);
        Swal.fire('Error', 'Could not load required permission data.', 'error');
        this.dialogRef.close();
      }
    });
  }

  togglePermission(id: number, checked: boolean) {
    if (checked) {
      if (!this.selectedPermissions.includes(id)) {
        this.selectedPermissions.push(id);
      }
    } else {
      this.selectedPermissions = this.selectedPermissions.filter((pid) => pid !== id);
    }
  }

  isPermissionSelected(id: number): boolean {
  
    return this.selectedPermissions.includes(id);
  }


save() {
  if (this.form.invalid) {
    return;
  }
  this.loading = true;
    const roleData = { name: this.form.value.name };
  
    if (this.data?.id) {
    // --- UPDATE ROLE ---
    this.roleService.updateRole(this.data.id, roleData).pipe(
      concatMap(() =>
        // After updating the role name, assign the selected permissions.
        this.roleService.assignPermissionsToRole(this.data.id!, this.selectedPermissions)
      ),
      finalize(() => (this.loading = false))
    ).subscribe({
      next: () => {
        Swal.fire('Success', 'Role and permissions updated successfully.', 'success');
        this.dialogRef.close(true);
        },
        error: (err) => {
          console.error('Update failed', err);
          Swal.fire('Error', 'Failed to update role or permissions.', 'error');
        },
      });
    } else {
       // --- CREATE ROLE ---
      // IMPORTANT: This relies on the backend createRole endpoint returning the new role object with its ID.
      this.roleService.createRole(roleData).pipe(
        concatMap((newRole: Role) => {
          const roleId = newRole?.id;
          if (!roleId) {
            throw new Error('Role created, but role ID was not returned.');
          }
                  // Now that we have the new role's ID, we can assign permissions.
                  return this.roleService.assignPermissionsToRole(roleId, this.selectedPermissions);
                }),
                finalize(() => (this.loading = false))
              ).subscribe({
                next: () => {
                  Swal.fire('Success', 'Role created and permissions assigned successfully.', 'success');
                  this.dialogRef.close(true);
        },
        error: (err) => {
          console.error('Role creation or permission assignment failed', err);
          const message = err?.error?.message || err?.message || 'An unknown error occurred.';
          Swal.fire('Operation Failed', message, 'error');
        },
      });
    }
  }
  
  
}
